/**
 * D3/PR-4 — race-recap 3-stage tab bar (footer-positioned).
 *
 * The tabs live inside the footer (above the confirm button) so the stage ①
 * race board sits at the same Y as the in-game / round-recap board, instead of
 * being pushed down by a top-of-body tab strip. During autoplay the tabs are
 * disabled and act as a progress indicator (current stage = playing, top-edge
 * accent bar pulsing); after autoplay completes the tabs become clickable so
 * the user can revisit any stage in its final state.
 *
 * Design contract: docs/wireframes/horror-race/states/race-recap.html `.stage-tabs`.
 */

export type StageTabState = "pending" | "playing" | "done";
export type StageTabId = 1 | 2 | 3;

interface StageTabsProps {
  readonly activeStage: StageTabId;
  readonly stageStates: Readonly<Record<StageTabId, StageTabState>>;
  readonly autoplayDone: boolean;
  readonly onTabClick: (stage: StageTabId) => void;
}

interface TabDef {
  readonly id: StageTabId;
  readonly label: string;
}

const TABS: readonly TabDef[] = [
  { id: 1, label: "① 순위" },
  { id: 2, label: "② 공개" },
  { id: 3, label: "③ 점수" },
];

export function StageTabs({
  activeStage,
  stageStates,
  autoplayDone,
  onTabClick,
}: StageTabsProps) {
  return (
    <div
      data-testid="recap-stage-tabs"
      data-autoplay-done={autoplayDone}
      role="tablist"
      aria-label="결산 단계"
      className="mb-2.5 flex overflow-hidden rounded-xl"
      style={{
        background: "var(--cn-horror-surface)",
        border: "1px solid var(--cn-horror-surface-border)",
      }}
    >
      {TABS.map((tab, idx) => {
        const state = stageStates[tab.id];
        const active = activeStage === tab.id;
        const isLast = idx === TABS.length - 1;
        const accent = active || state === "playing";
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid="recap-stage-tab"
            data-tab={tab.id}
            data-active={active}
            data-state={state}
            disabled={!autoplayDone}
            onClick={() => {
              if (autoplayDone) onTabClick(tab.id);
            }}
            className="relative flex-1 px-1.5 py-2.5 font-bold text-[12px] transition-[background,color,opacity]"
            style={{
              background: active ? "var(--cn-horror-card-bg)" : "transparent",
              border: 0,
              borderRight: isLast
                ? "none"
                : "1px solid var(--cn-horror-surface-border)",
              color:
                state === "playing"
                  ? "var(--cn-horror-p3)"
                  : active
                    ? "var(--cn-horror-text)"
                    : "var(--cn-horror-text-muted)",
              cursor: autoplayDone ? "pointer" : "not-allowed",
              opacity: autoplayDone ? 1 : 0.55,
            }}
          >
            {/* Top-edge accent — solid on active, pulsing on playing. */}
            {accent && (
              <span
                aria-hidden="true"
                className={
                  state === "playing" ? "hr-stage-tab-pulse" : undefined
                }
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  backgroundColor: "var(--cn-horror-p3)",
                }}
              />
            )}
            {state === "done" && (
              <span
                aria-hidden="true"
                style={{
                  color: "var(--cn-horror-finished-flash)",
                  fontSize: 10,
                  fontWeight: 900,
                  marginRight: 3,
                  verticalAlign: 1,
                }}
              >
                ✓
              </span>
            )}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
